import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { PageLoader, EmptyState, SectionHeader } from '../components/common/UI';
import { toShamsiWithDay } from '../utils/dateHelpers';
import { useAuth } from '../context/AuthContext';
import { MdSend, MdMessage } from '../utils/icons';

export default function Communications() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState('');
  const [msgType, setMsgType] = useState('InternalChat');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedProject) params.append('project', selectedProject);
      if (msgType) params.append('type', msgType);
      const [msgRes, projRes] = await Promise.all([
        API.get(`/communications?${params}`),
        API.get('/projects'),
      ]);
      setMessages(msgRes.data);
      setProjects(projRes.data);
    } finally { setLoading(false); }
  }, [selectedProject, msgType]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await API.post('/communications', {
        message: text.trim(),
        project: selectedProject || undefined,
        type: msgType,
      });
      setText('');
      load();
    } catch { toast.error('خطا در ارسال'); } finally { setSending(false); }
  };

  const typeLabels = {
    InternalChat: '💬 چت داخلی',
    ClientFeedback: '👤 فیدبک مشتری',
    ProjectComment: '📁 کامنت پروژه',
  };

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader title="ارتباط" subtitle="چت تیم و فیدبک مشتریان" />

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <select value={msgType} onChange={e => setMsgType(e.target.value)} className="form-input sm:w-48">
            {Object.entries(typeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} className="form-input flex-1">
            <option value="">همه پروژه‌ها</option>
            {projects.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {/* Chat area */}
      <div className="glass-card flex flex-col h-[500px]">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 ? (
            <EmptyState icon="💬" title="پیامی یافت نشد" subtitle="اولین پیام را ارسال کنید" />
          ) : (
            messages.map(msg => {
              const isMe = msg.sender?._id === user?.id || msg.sender?.name === user?.name;
              return (
                <div key={msg._id} className={`flex ${isMe ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[80%] ${isMe ? 'items-start' : 'items-end'} flex flex-col`}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                        {msg.sender?.name?.charAt(0) || '?'}
                      </div>
                      <span className="text-xs text-gray-500">{msg.sender?.name}</span>
                      {msg.project && <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-md">📁 {msg.project?.name}</span>}
                    </div>
                    <div className={`px-4 py-2.5 rounded-2xl text-sm ${isMe ? 'bg-blue-100 text-gray-800 rounded-tr-sm' : 'bg-gray-100 text-gray-700 rounded-tl-sm'}`}>
                      {msg.message}
                    </div>
                    <span className="text-xs text-gray-600 mt-1">{toShamsiWithDay(msg.createdAt)}</span>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 p-4">
          <form onSubmit={handleSend} className="flex gap-3">
            <input
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder={`${typeLabels[msgType]}...`}
              className="form-input flex-1"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !text.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-xl transition-colors flex items-center gap-2"
            >
              <MdSend size={16} />
              <span className="hidden sm:inline">ارسال</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
